import React, { useEffect } from 'react';

import { graphql } from 'gatsby';
import type { PageProps } from 'gatsby';
import styled from 'styled-components';

import { ArticleListingUrlContextProvider } from 'context/articleListingUrl.context';

import { ContentstackBrowserPlugin } from 'components/ContentstackBrowserPlugin/ContentstackBrowserPlugin';
import Breadcrumbs from 'components/Layout/Header/components/Breadcrumbs/Breadcrumbs';
import Layout from 'components/Layout/Layout';
import ModularBlocks from 'components/ModularBlocks';
import PriceTicker from 'components/PriceTicker';
import SEO from 'components/SEO';
import StaticMasthead from 'components/StaticMasthead/StaticMasthead';

import { useContentstackData } from 'hooks/useContentstackData';
import useScroll from 'hooks/useScroll';

import { SeoI } from 'interfaces/content-stack/blocks/seo';
import { BlogPostI } from 'interfaces/content-stack/blogPost';
import { BreadcrumbI, MastheadT2 } from 'interfaces/content-stack/contentStack.types';
import { ModularBlockI } from 'interfaces/content-stack/modular-blocks/modularBlocks.types';

import { pushSpecialPageEvent } from 'utils/dataLayerUtils';
import { dataLayerPush } from 'utils/functions';

const StyledMain = styled.main<{ $scrollUp?: boolean; $scrollY: number }>`
  min-height: 100vh;
  @media (min-width: ${(props) => props.theme.breakpoints.lg}) {
    transition: all 0.5s;

    transform: ${(props) => (props.$scrollUp && props.$scrollY < 100 ? 'translateY(-2.3rem)' : 'none')};
  }
`;

interface UtilityPageQueryProps {
  contentstackUtilityPage: {
    cslp__meta: Record<string, any>;
    uid: string;
    url: string;
    title: string;
    tags?: string;
    locale: string;
    typename: string;
    pageType: {
      title: string;
    }[];
    toggleTicker: boolean;
    hasMasthead: boolean;
    masthead: MastheadT2;
    breadcrumb: BreadcrumbI[];
    pageBody: ModularBlockI[];
    seo: SeoI;
  };
  allContentstackArticles: {
    edges: {
      node: BlogPostI[];
    }[];
  }[];
}

type UtilityProps = PageProps<UtilityPageQueryProps, { allArticles: string }>;

const utilityPage: React.FC<UtilityProps> = ({ data, pageContext }) => {
  const { contentstackUtilityPage: initPageData, allContentstackArticles: articles } = data;
  const page = useContentstackData(initPageData);
  const { breadcrumb, masthead, hasMasthead, pageBody, seo, title, url, pageType, tags, uid, locale, typename } = page;
  const { scrollDirection, scrollY } = useScroll();
  const typeCategory = pageType?.[0]?.title;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      dataLayerPush('page_data_loaded', 'event', {
        content_language: 'en',
        language: 'en_US',
        page_category: 'Utility',
        page_name: title,
        page_tags: tags,
        page_type: typeCategory,
      });
      pushSpecialPageEvent(url, title);
    }
  }, [pageType, url, title]);

  return (
    <Layout>
      <SEO seo={seo} title={title} />
      <ContentstackBrowserPlugin attributes={{ uid, locale, typename }} />
      <StyledMain $scrollUp={scrollDirection === 'up'} $scrollY={scrollY}>
        <Breadcrumbs breadcrumbs={breadcrumb} page={{ title, url }} scrollDirection={scrollDirection} />
        {!page.toggleTicker && <PriceTicker />}
        {hasMasthead && masthead && <StaticMasthead masthead={masthead} />}
        <ArticleListingUrlContextProvider allArticles={pageContext.allArticles}>
          <ModularBlocks blogPost={articles} modularBlocks={pageBody} />
        </ArticleListingUrlContextProvider>
      </StyledMain>
    </Layout>
  );
};

export const query = graphql`
  query ($id: String, $blogPostCategoryUid: [String]) {
    contentstackUtilityPage(id: { eq: $id }) {
      cslp__meta
      uid
      url
      title
      tags
      locale
      typename: __typename
      pageType: page_type {
        title
      }
      toggleTicker: toggle_ticker
      breadcrumb {
        ... on Contentstack_brochure_page {
          url
          title
        }
        ... on Contentstack_utility_page {
          title
          url
        }
      }
      pageBody: page_body {
        twoBlockContent: two_block_content {
          ...TwoBlockContentFragmentUtility
        }
      }
      ...UtilityMastheadFragment
      pageBody: page_body {
        flexibleTabbedTable: flexible_tabbed_table {
          ...UtilityFlexibleTabbedBlockFragment
        }
        contentCards: content_cards {
          ...UtilityPageContentCardsFragment
        }
        disclaimer {
          ...UtilityPageDisclaimerFragment
        }
        textBlock: text_block {
          ...UtilityPageTextBlockFragment
        }
        accordionBlock: accordion_block {
          ...AccordionFragmentUtility
        }
        articleFeedBlock: article_feed_block {
          ...UtilityArticlesFeedBlockFragment
        }
        awardsBlock: awards_block {
          ...UtilityAwardsBlockFragment
        }
        tabTable: tab_table {
          ...TabTableFragmentUtility
        }
        basicTableBlock: basic_table_block {
          ...UtilityBasicTableBlockFragment
        }
        fullBleedCta: full_bleed_cta {
          ...UtilityPageFullBleedCtaFragment
        }
        watchBlock: watch_block {
          ...WatchBlockFragmentUtility
        }
        livePricingBlock: live_pricing_block {
          ...UtilityLivePricingFragment
        }
        documentBlock: document_block {
          ...UtilityPageDocumentFragment
        }
        spacer {
          ...UtilityPageSpacerFragment
        }
        blockGroupStart: block_group_start {
          ...UtilityPageBlockGroupStartFragment
        }
        blockGroupEnd: block_group_end {
          ...UtilityPageBlockGroupEndFragment
        }
        basicTextBlock: basic_text_block {
          ...UtilityPageBasicTextBlockFragment
        }
        iconCardBlock: icon_card_block {
          ...UtilityIconCardBlockFragment
        }
        comparisonTable: comparison_table {
          ...UtilityComparisonTableFragment
        }
        articlesBlock: articles_block {
          ...UtilityPageArticlesBlockFragment
        }
        contactForm: contact_form {
          ...UtilityContactFormFragment
        }
        demoForm: demo_form {
          ...UtilityDemoFormFragment
        }
        mfeBlock: mfe_block {
          ...UtilityMfeBlockFragment
        }
      }
      seo {
        ...UtilityPageSeoFragment
      }
    }
    allContentstackArticles(
      sort: { post_date: DESC }
      filter: { metadata: { categories: { elemMatch: { uid: { in: $blogPostCategoryUid } } } } }
    ) {
      edges {
        node {
          title
          postDate: post_date
          url
          toggleTicker: toggle_ticker
          breadcrumb {
            title
            url
          }
          metadata {
            excerpt
            thumbnailImage: thumbnail_image {
              url
            }
            categories {
              title
              uid
            }
          }
        }
      }
    }
  }
`;

export default utilityPage;
